import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactSelect, { components as ReactSelectComponents } from "react-select";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { WithTooltip } from "../../../utils/glossaryHelper/GlossaryWithTooltip";
import { getMyRequestedCapitalRequestFilterLabel } from "../../../utils/capitalRequestLabel";
import Loader from "../../common/components/Loader";
import store from "../../../store/configureStore";
import { REVIEW_GATE_STATUS_LABELS } from "../utils/qaGateValidation";

const Bar = styled.div`
    padding: 2px 0;
    width: 100%;
    overflow-x: auto;
`;

const FiltersList = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    list-style: none;
    margin: 0;
    padding: 0;
`;

const FilterItem = styled.li`
    && {
        display: flex;
        flex-direction: column;
        gap: 1px;
        font-size: 13px;
        padding: 4px;
        border-radius: 4px;
        background-color: ${({ $active }) => ($active ? "#5498ff54" : "transparent")} !important;
    }

    ${({ $isSingle }) =>
        $isSingle
            ? `
        flex: 0 0 260px;
        max-width: 260px;
        min-width: 130px;
    `
            : `
        flex: 1 1 170px;
        min-width: 130px;
    `}
`;

const FilterLabel = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #555;
    line-height: 2;
`;

const DropdownLoader = styled.div`
    position: relative;
    min-height: 48px;
`;

const OptionWithCount = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
`;

const OptionLabel = styled.span`
    display: block;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const OptionCount = styled.span`
    flex: 0 0 auto;
    color: inherit;
`;

const DropdownOptionTooltip = styled(ReactTooltip)`
    && {
        z-index: 100000 !important;
    }
`;

const LoadingMenuList = props => {
    const { children, selectProps } = props;

    return (
        <ReactSelectComponents.MenuList {...props}>
            {selectProps?.loading ? (
                <DropdownLoader>
                    <Loader />
                </DropdownLoader>
            ) : (
                children
            )}
        </ReactSelectComponents.MenuList>
    );
};

const selectComponents = {
    MenuList: LoadingMenuList
};

const selectStyles = {
    container: base => ({
        ...base,
        width: "100%",
        minWidth: "130px",
        zIndex: 5
    }),
    menu: base => ({
        ...base,
        zIndex: 9999,
        fontSize: 13
    }),
    menuPortal: base => ({
        ...base,
        zIndex: 99999
    }),
    control: base => ({
        ...base,
        minHeight: 24,
        fontSize: 12,
        lineHeight: 1.2,
        paddingTop: 0,
        paddingBottom: 0
    }),
    valueContainer: base => ({
        ...base,
        paddingTop: 0,
        paddingBottom: 0
    }),
    indicatorsContainer: base => ({
        ...base,
        height: 24
    }),
    dropdownIndicator: base => ({
        ...base,
        paddingTop: 2,
        paddingBottom: 2
    }),
    clearIndicator: base => ({
        ...base,
        paddingTop: 2,
        paddingBottom: 2
    }),
    option: (base, state) => ({
        ...base,
        color: state.isSelected ? "#fff" : base.color
    })
};

const COUNT_SUFFIX_REGEX = /\s*\(\d+(?:\/\d+)?\)\s*$/;
const OPTION_TOOLTIP_ID = "recommendation-more-filter-option-tooltip";

const stripCountFromLabel = label => (typeof label === "string" ? label.replace(COUNT_SUFFIX_REGEX, "") : label);

const DropdownOptionLabel = ({ label }) => {
    const textRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipLabel = label === null || label === undefined ? "" : String(label);

    useLayoutEffect(() => {
        const textElement = textRef?.current;
        const isEllipsisActive = Boolean(textElement && textElement?.scrollWidth > textElement?.clientWidth);
        let animationFrameId;

        setShowTooltip(isEllipsisActive);
        if (isEllipsisActive) {
            animationFrameId = window.requestAnimationFrame(() => ReactTooltip.rebuild());
        }

        return () => {
            if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
            ReactTooltip.hide();
        };
    }, [tooltipLabel]);

    return (
        <OptionLabel ref={textRef} data-for={OPTION_TOOLTIP_ID} data-tip={showTooltip ? tooltipLabel : null}>
            <DropdownOptionTooltip
                id={OPTION_TOOLTIP_ID}
                className="rc-tooltip-custom-class"
                backgroundColor="#007bff"
                place="bottom"
                effect="solid"
            />
            {label}
        </OptionLabel>
    );
};

const getOptionCount = (option, countList) => {
    if (!option || option.value === null || option.value === "all") return undefined;
    const optionValue = option.value;
    const match = countList.find(item => optionValue === item?.id);

    const count = Number(match?.count);
    return match?.count !== null && match?.count !== undefined && !Number.isNaN(count) ? count : 0;
};

const getCaptionOptionCountLabel = ({ option, options }) => {
    if (option?.value !== false || typeof option.count !== "number") return undefined;
    const total = options.reduce((total, item) => (typeof item.count === "number" ? total + item.count : total), 0);

    return `${option.count}/${total}`;
};

const QA_STATUS_ALL_OPTION = { label: "All", value: null };

const QA_STATUS_LIST_PARAMS = { include_all: true };

const useCommonFilterOptionCounts = ({ getListForCommonFilterRecommendation, field, searchKey, options, buildOptionsFromList, extraParams }) => {
    const [countList, setCountList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const fieldKey = field;

    const handleMenuOpen = useCallback(async () => {
        if (isLoading || !getListForCommonFilterRecommendation) return;

        setIsLoading(true);
        try {
            await getListForCommonFilterRecommendation({
                field: fieldKey,
                searchKey,
                ...extraParams
            });
            setCountList(
                searchKey === "opinions_cost_vs_project_total"
                    ? store.getState().recommendationsReducer.getOpinionsFilterResponse?.list
                    : store.getState().recommendationsReducer.getListForCommonFilterResponse?.list || []
            );
        } catch (err) {
            setCountList([]);
        } finally {
            setIsLoading(false);
        }
    }, [fieldKey, getListForCommonFilterRecommendation, isLoading, searchKey, extraParams]);

    const optionsWithCounts = useMemo(() => {
        // When the option list itself is served by the backend (id + label + count),
        // derive the options straight from the fetched count list.
        if (typeof buildOptionsFromList === "function") {
            return buildOptionsFromList(countList || []);
        }
        const countedOptions = (options || []).map(option => {
            const nextOption = {
                ...option,
                label: stripCountFromLabel(option.label)
            };
            const count = getOptionCount(nextOption, countList);
            delete nextOption.count;
            delete nextOption.countLabel;
            if (typeof count === "number") nextOption.count = count;
            return nextOption;
        });

        return countedOptions.map(option => {
            const countLabel = getCaptionOptionCountLabel?.({ option, countList, options: countedOptions });
            if (countLabel === null || countLabel === undefined) return option;

            return {
                ...option,
                countLabel
            };
        });
    }, [countList, options, buildOptionsFromList]);

    const formatOptionLabel = useCallback((option, { context }) => {
        const label = stripCountFromLabel(option?.label);
        const countLabel = option?.countLabel ?? option?.count;
        if (context !== "menu") return label;

        return typeof countLabel === "number" || typeof countLabel === "string" ? (
            <OptionWithCount>
                <DropdownOptionLabel label={label} />
                <OptionCount>({countLabel})</OptionCount>
            </OptionWithCount>
        ) : (
            <DropdownOptionLabel label={label} />
        );
    }, []);

    const cleanValue = useCallback(value => {
        if (Array.isArray(value)) {
            return value.map(item => (item ? { ...item, label: stripCountFromLabel(item.label) } : item));
        }
        return value ? { ...value, label: stripCountFromLabel(value.label) } : value;
    }, []);

    return {
        options: optionsWithCounts,
        onMenuOpen: handleMenuOpen,
        isLoading,
        formatOptionLabel,
        cleanValue
    };
};

const STATUS_OPTIONS = [
    { value: "all", label: "All" },
    { value: "locked", label: "Locked" },
    { value: "unlocked", label: "Unlocked" }
];

const IMAGE_YES_NO_ALL = [
    { value: null, label: "All" },
    { value: "true", label: "Yes" },
    { value: "false", label: "No" }
];

const SURVEYOR_OPTIONS = [
    { value: null, label: "All" },
    { value: "me", label: "My surveyed recommendations" }
];

const CAPTION_OPTIONS = [
    { label: "Image(s) caption missing", value: true },
    { label: "Image(s) caption present", value: false }
];

export const MoreFilters = ({
    isBudgetPriority,
    isCapitalRequest,
    isSmartChartView,
    viewOnly,
    section,

    hasViewMyRecommendation,
    hasFilterByMyQa,
    hasFilterByQaStatus,
    isConsultancyUser,
    isSuperAdmin,
    miscellaneous,

    tableParams = {},
    opinionCostOptions = [],
    selectedOpinionValue,
    selectedDropdown,

    onStatusChange,
    filterBySurveyor,
    filterByAnyKey,
    filterByOpinionsCost,
    filterByReviews,
    filterByQaStatus,
    getListForCommonFilterRecommendation
}) => {
    const isConsultancyOrAdmin = isConsultancyUser || isSuperAdmin;
    const useCompactFilterWidth = !isConsultancyOrAdmin;
    const disableAll = viewOnly;

    const isFilterActive = key => {
        const v = tableParams[key];
        return v !== null && v !== undefined && v !== "" && v !== "all";
    };

    const ownerFilterKey = isCapitalRequest ? "requestor" : "surveyor";
    const ownerFilterOptions = useMemo(() => {
        if (!isCapitalRequest) return SURVEYOR_OPTIONS;
        return [
            { value: null, label: "All" },
            { value: "me", label: getMyRequestedCapitalRequestFilterLabel(miscellaneous) }
        ];
    }, [isCapitalRequest, miscellaneous]);

    const lockStatusFilter = useCommonFilterOptionCounts({
        getListForCommonFilterRecommendation,
        field: "recommendations.lock_status",
        searchKey: "lock_status",
        options: STATUS_OPTIONS
    });

    const imageFilter = useCommonFilterOptionCounts({
        getListForCommonFilterRecommendation,
        field: "recommendations.image_or_not",
        searchKey: "image_or_not",
        options: IMAGE_YES_NO_ALL
    });
    const ownerFilter = useCommonFilterOptionCounts({
        getListForCommonFilterRecommendation,
        field: "recommendations.my_surveyed",
        searchKey: ownerFilterKey,
        options: ownerFilterOptions
    });

    const captionFilter = useCommonFilterOptionCounts({
        getListForCommonFilterRecommendation,
        field: "recommendation_images.description",
        searchKey: "recommendation_images.description",
        options: [{ label: "All", value: null }, ...CAPTION_OPTIONS]
    });

    const opinionCostFilter = useCommonFilterOptionCounts({
        getListForCommonFilterRecommendation,
        field: ["project_total", "opinions_cost"],
        searchKey: "opinions_cost_vs_project_total",
        options: opinionCostOptions
    });

    const myQaFilter = useCommonFilterOptionCounts({
        getListForCommonFilterRecommendation,
        field: "recommendations.review_status",
        searchKey: "review_status",
        options: statusOptions
    });

    const qaStatusFilter = useCommonFilterOptionCounts({
        getListForCommonFilterRecommendation,
        field: "recommendations.qa_qc_status",
        searchKey: "qa_qc_status",
        buildOptionsFromList: buildQaStatusOptions,
        extraParams: QA_STATUS_LIST_PARAMS
    });

    const statusValue = lockStatusFilter.cleanValue(STATUS_OPTIONS.find(opt => opt.value === selectedDropdown) || STATUS_OPTIONS[0]);

    const imageValue = imageFilter.cleanValue(IMAGE_YES_NO_ALL.find(opt => opt.value === (tableParams.image_or_not ?? null)) || IMAGE_YES_NO_ALL[0]);

    const captionValue = captionFilter.cleanValue(CAPTION_OPTIONS.find(opt => opt.value === tableParams.caption_missing) || null);

    const myQaValue = myQaFilter.cleanValue(statusOptions.find(opt => opt.value === tableParams.review_status) || null);

    const qaStatusValue = qaStatusFilter.cleanValue(
        tableParams.qa_qc_status
            ? {
                  value: tableParams.qa_qc_status,
                  label: REVIEW_GATE_STATUS_LABELS[tableParams.qa_qc_status] || tableParams.qa_qc_status
              }
            : qaStatusFilter.options.find(opt => opt.value === null) || QA_STATUS_ALL_OPTION
    );

    const ownerFilterValue = ownerFilter.cleanValue(
        ownerFilterOptions.find(opt => {
            if (tableParams[ownerFilterKey]) return opt.value === "me";
            return opt.value === null;
        }) || ownerFilterOptions[0]
    );

    const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

    return (
        <Bar>
            <FiltersList>
                {!isBudgetPriority && !isCapitalRequest && (
                    <FilterItem $isSingle={useCompactFilterWidth} $active={isFilterActive("locked")}>
                        <FilterLabel>Lock Status</FilterLabel>
                        <ReactSelect
                            styles={selectStyles}
                            components={selectComponents}
                            menuPortalTarget={menuPortalTarget}
                            classNamePrefix="react-select"
                            options={lockStatusFilter.options}
                            value={statusValue}
                            onMenuOpen={lockStatusFilter.onMenuOpen}
                            loading={lockStatusFilter.isLoading}
                            formatOptionLabel={lockStatusFilter.formatOptionLabel}
                            onChange={opt =>
                                onStatusChange?.({
                                    target: { value: opt?.value || "all" }
                                })
                            }
                            isDisabled={isSmartChartView || viewOnly || section === "initiativeInfo"}
                            placeholder="Select"
                        />
                    </FilterItem>
                )}

                {isConsultancyOrAdmin && (
                    <FilterItem $active={isFilterActive("image_or_not")}>
                        <FilterLabel>Image Present</FilterLabel>
                        <ReactSelect
                            styles={selectStyles}
                            components={selectComponents}
                            menuPortalTarget={menuPortalTarget}
                            classNamePrefix="react-select"
                            options={imageFilter.options}
                            value={imageValue}
                            onMenuOpen={imageFilter.onMenuOpen}
                            loading={imageFilter.isLoading}
                            formatOptionLabel={imageFilter.formatOptionLabel}
                            onChange={opt => filterByAnyKey?.("image_or_not", opt ? opt.value : null)}
                            isDisabled={disableAll}
                            placeholder="Select"
                        />
                    </FilterItem>
                )}

                {hasViewMyRecommendation && (
                    <FilterItem $isSingle={useCompactFilterWidth} $active={isFilterActive(ownerFilterKey)}>
                        <FilterLabel>{isCapitalRequest ? "Requestor" : "Surveyor"}</FilterLabel>
                        <ReactSelect
                            styles={selectStyles}
                            components={selectComponents}
                            menuPortalTarget={menuPortalTarget}
                            classNamePrefix="react-select"
                            options={ownerFilter.options}
                            value={ownerFilterValue}
                            onMenuOpen={ownerFilter.onMenuOpen}
                            loading={ownerFilter.isLoading}
                            formatOptionLabel={ownerFilter.formatOptionLabel}
                            onChange={opt => {
                                const val = opt ? opt.value : null;
                                filterBySurveyor?.(val);
                            }}
                            isDisabled={disableAll}
                            placeholder="Select"
                        />
                    </FilterItem>
                )}

                {isConsultancyOrAdmin && (
                    <FilterItem $active={isFilterActive("caption_missing")}>
                        <FilterLabel>Rec. and Image Caption</FilterLabel>
                        <ReactSelect
                            styles={selectStyles}
                            components={selectComponents}
                            menuPortalTarget={menuPortalTarget}
                            classNamePrefix="react-select"
                            options={captionFilter.options}
                            value={captionValue || { label: "All", value: null }}
                            onMenuOpen={captionFilter.onMenuOpen}
                            loading={captionFilter.isLoading}
                            formatOptionLabel={captionFilter.formatOptionLabel}
                            placeholder="Select"
                            onChange={opt => filterByAnyKey?.("caption_missing", opt ? opt.value : null)}
                            isOptionDisabled={() => disableAll}
                            noOptionsMessage={() => (captionFilter.isLoading ? "Loading..." : "No options")}
                        />
                    </FilterItem>
                )}

                {isConsultancyOrAdmin && miscellaneous?.display_unit && !isSmartChartView && (
                    <FilterItem $active={isFilterActive("opinions_cost_vs_project_total")}>
                        <FilterLabel>
                            <WithTooltip>TOC</WithTooltip> vs Construction Cost
                        </FilterLabel>
                        <ReactSelect
                            styles={selectStyles}
                            components={selectComponents}
                            menuPortalTarget={menuPortalTarget}
                            options={opinionCostFilter.options}
                            value={opinionCostFilter.cleanValue(selectedOpinionValue || null)}
                            classNamePrefix="react-select"
                            placeholder="Select"
                            onMenuOpen={opinionCostFilter.onMenuOpen}
                            loading={opinionCostFilter.isLoading}
                            formatOptionLabel={opinionCostFilter.formatOptionLabel}
                            onChange={selectedOption => {
                                const value = selectedOption ? selectedOption.value : null;
                                filterByOpinionsCost?.(value);
                            }}
                            isOptionDisabled={() => disableAll}
                        />
                    </FilterItem>
                )}

                {hasFilterByMyQa && isConsultancyOrAdmin && (
                    <FilterItem $active={isFilterActive("review_status")}>
                        <FilterLabel>My QA</FilterLabel>
                        <ReactSelect
                            styles={selectStyles}
                            components={selectComponents}
                            menuPortalTarget={menuPortalTarget}
                            options={myQaFilter.options}
                            value={myQaValue}
                            classNamePrefix="react-select"
                            placeholder="Select"
                            onMenuOpen={myQaFilter.onMenuOpen}
                            loading={myQaFilter.isLoading}
                            formatOptionLabel={myQaFilter.formatOptionLabel}
                            onChange={opt => filterByReviews?.(opt ? opt.value : null)}
                            isOptionDisabled={() => disableAll}
                        />
                    </FilterItem>
                )}

                {hasFilterByQaStatus && isConsultancyOrAdmin && (
                    <FilterItem $active={isFilterActive("qa_qc_status")}>
                        <FilterLabel>QA Status</FilterLabel>
                        <ReactSelect
                            styles={selectStyles}
                            components={selectComponents}
                            menuPortalTarget={menuPortalTarget}
                            options={qaStatusFilter.options}
                            value={qaStatusValue}
                            classNamePrefix="react-select"
                            placeholder="Select"
                            onMenuOpen={qaStatusFilter.onMenuOpen}
                            loading={qaStatusFilter.isLoading}
                            formatOptionLabel={qaStatusFilter.formatOptionLabel}
                            onChange={opt => filterByQaStatus?.(opt ? opt.value : null)}
                            isOptionDisabled={() => disableAll}
                            noOptionsMessage={() => (qaStatusFilter.isLoading ? "Loading..." : "No options")}
                        />
                    </FilterItem>
                )}
            </FiltersList>
        </Bar>
    );
};

const statusOptions = [
    { label: "All", value: null },
    { label: "Pending", value: "pending" },
    { label: "Resolved", value: "resolved" },
    { label: "Approved", value: "approved" },
    { label: "All Pending", value: "all_pending" },
    { label: "All Resolved", value: "all_resolved" },
    { label: "All Approved", value: "all_approved" },
    { label: "No QA/QC Issues", value: "no_reviews" }
];

// QA Status filter options are served by the backend get_list endpoint
// (field recommendations.qa_qc_status). Each item is { id, name, count }, where id is the
// qa_status enum string (or null for the "All" row, which the API includes itself).
const buildQaStatusOptions = (countList = []) =>
    countList.map(item => {
        const isAll = item?.id === null || item?.id === undefined;
        const count = Number(item?.count);
        const option = {
            value: item?.id ?? null,
            label: item?.name || REVIEW_GATE_STATUS_LABELS[item?.id] || String(item?.id ?? "")
        };
        if (!isAll && !Number.isNaN(count)) option.count = count;
        return option;
    });
